import React from 'react';
import { View, Text, Image, StyleSheet, TouchableOpacity, ViewStyle, Platform } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { Colors, Typography, Layout, Shadows } from '@cubemeet/core';
import { getAvatarSource } from '@cubemeet/core';
import { getSharedImageAsset } from '../../assets/imageRegistry';
import { getPresetAvatarAsset } from '../../assets/avatarRegistry';

interface AvatarProps {
  size?: 'small' | 'medium' | 'large' | 'xl' | 'xxl' | '100';
  avatarUrl?: string | null;
  displayName?: string | null;
  email?: string;
  onPress?: () => void;
  showEditIcon?: boolean;
  showBorder?: boolean;
  showOnlineStatus?: boolean;
  isOnline?: boolean;
  style?: ViewStyle;
}

const Avatar: React.FC<AvatarProps> = ({
  size = 'medium',
  avatarUrl,
  displayName,
  email,
  onPress,
  showEditIcon = false,
  showBorder = false,
  showOnlineStatus = false,
  isOnline = false,
  style,
}) => {
  // 使用统一的头像映射器
  const getAvatarDisplaySource = (url: string | null | undefined) => {
    if (!url) return null;
    // 优先尝试获取预设头像 (解决循环依赖问题)
    const presetAsset = getPresetAvatarAsset(url);
    if (presetAsset) return presetAsset;
    
    return getAvatarSource(url);
  };

  // 尺寸配置
  const sizeConfig = {
    small: { 
      size: Layout.avatarSize.small, 
      fontSize: Typography.fontSize.sm,
      editIconSize: 16,
      statusSize: 8,
    },
    medium: { 
      size: Layout.avatarSize.medium, 
      fontSize: Typography.fontSize.base,
      editIconSize: 18,
      statusSize: 10,
    },
    large: { 
      size: Layout.avatarSize.large, 
      fontSize: Typography.fontSize.xl,
      editIconSize: 20,
      statusSize: 12,
    },
    xl: { 
      size: Layout.avatarSize.xl, 
      fontSize: Typography.fontSize['2xl'],
      editIconSize: 28,
      statusSize: 14,
    },
    xxl: {
      size: 100, 
      fontSize: Typography.fontSize['3xl'],
      editIconSize: 32,
      statusSize: 16,
    },
    '100': {
      size: 100, 
      fontSize: Typography.fontSize['3xl'],
      editIconSize: 32,
      statusSize: 16,
    },
  };

  const config = sizeConfig[size] || sizeConfig.medium;
  
  // 获取显示的首字母
  const getInitials = () => {
    if (displayName) {
      const names = displayName.trim().split(' ');
      if (names.length > 1) {
        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
      }
      return displayName.charAt(0).toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // 生成基于名字的背景色
  const getBackgroundColor = () => {
    const name = displayName || email || 'User';
    const colors = [
      Colors.primary.main,
      Colors.secondary.main,
      Colors.info,
      Colors.warning,
      '#8B5CF6', // purple
      '#06B6D4', // cyan
      '#10B981', // emerald
      '#F59E0B', // amber
      '#EF4444', // red
      '#8B5A2B', // brown
      '#6B7280', // gray
      '#EC4899', // pink
    ];
    
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  const avatarStyle = [
    styles.avatar,
    {
      width: config.size,
      height: config.size,
      borderRadius: config.size / 2,
      backgroundColor: avatarUrl ? Colors.neutral.gray100 : getBackgroundColor(),
    },
    showBorder && styles.avatarBorder,
    // apply platform-specific shadow: use CSS boxShadow on web, native shadow props on other platforms
    showBorder && (Platform.select ? Platform.select({ web: { boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }, default: Shadows.small }) : Shadows.small),
    style,
  ];

  const Container = onPress ? TouchableOpacity : View;

  return (
    <Container 
      style={styles.container} 
      onPress={onPress}
      activeOpacity={onPress ? 0.8 : 1}
    >
      <View style={avatarStyle}>
        {avatarUrl ? (
          <Image
            source={getAvatarDisplaySource(avatarUrl) || { uri: avatarUrl }}
            style={[
              styles.avatarImage,
              {
                width: config.size,
                height: config.size,
                borderRadius: config.size / 2,
              },
            ]}
            resizeMode="cover"
            defaultSource={(getSharedImageAsset('assets/images/logo.png') as unknown) as any}
            onError={(error) => {
              console.log('Avatar头像加载失败:', error);
              console.log('avatarUrl:', avatarUrl);
            }}
          />
        ) : (
          <Text
            style={[
              styles.avatarText,
              {
                fontSize: config.fontSize,
                color: Colors.text.white,
                fontWeight: Typography.fontWeight.semibold,
              },
            ]}
          >
            {getInitials()}
          </Text>
        )}
        
        {/* 编辑图标 */}
        {showEditIcon && (
          <View 
            style={[
              styles.editIconContainer,
              // per-instance size + position
              {
                width: config.editIconSize + 8,
                height: config.editIconSize + 8,
                borderRadius: (config.editIconSize + 8) / 2,
                bottom: 0,
                right: 0,
              },
              // apply platform-specific shadow: css boxShadow on web, native Shadows.medium on other platforms
              Platform.select ? Platform.select({ web: { boxShadow: '0 2px 6px rgba(0,0,0,0.12)' }, default: Shadows.medium }) : Shadows.medium,
            ]}
          >
            <Icon 
              name="camera-alt" 
              size={config.editIconSize} 
              color={Colors.text.white} 
            />
          </View>
        )}
        
        {/* 在线状态指示器 */}
        {showOnlineStatus && (
          <View 
            style={[
              styles.statusIndicator,
              {
                width: config.statusSize,
                height: config.statusSize,
                borderRadius: config.statusSize / 2,
                backgroundColor: isOnline ? Colors.success : Colors.neutral.gray400,
                bottom: 2,
                right: 2,
              }
            ]}
          />
        )}
      </View>
    </Container>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  
  avatar: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  
  avatarBorder: {
    borderWidth: 2,
    borderColor: Colors.background.primary,
    // Shadows are applied per-platform at usage time to avoid react-native-web
    // deprecation warnings about shadow* props. See usage in avatarStyle.
  },
  
  avatarImage: {
    // resizeMode 已移至 Image 组件的 prop
  },
  
  avatarText: {
    textAlign: 'center',
    lineHeight: undefined,
  },
  
  editIconContainer: {
    position: 'absolute',
    backgroundColor: Colors.primary.main,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: Colors.background.primary,
    // apply shadows per-platform at usage time
  },
  
  statusIndicator: {
    position: 'absolute',
    borderWidth: 1,
    borderColor: Colors.background.primary,
  },
});

export default Avatar;
